import { Entity } from '@api/api/entity-schema';
import {
  Mention,
  MentionCandidate,
  MentionPopupSection,
  MentionQuery,
} from '@design/chat/mention-composer';
import { DropdownListItem } from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { Icon } from '@design/components/icon/Icon';
import { useQueryClient } from '@tanstack/react-query';
import { ListEntityItem } from '@terminal/components/search-modal/components/list-entity-item/ListEntityItem';
import { ListThemeItem } from '@terminal/components/search-modal/components/list-theme-item/ListThemeItem';
import { ListTitleItem } from '@terminal/components/search-modal/components/list-title-item/ListTitleItem';
import { Theme } from '@terminal/components/search-modal/utils';
import { THEME_FILL_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { getCachedThemeLogoUrl } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { AssetOverviewPreviewCard } from '@terminal/views/alfred/research/asset-overview-link/AssetOverviewPreviewCard';
import { ThemeOverviewPreviewCard } from '@terminal/views/alfred/research/theme-overview-link/ThemeOverviewPreviewCard';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  MentionAssetCandidate,
  MentionEntityCandidate,
  MentionThemeCandidate,
  MentionWatchlistCandidate,
} from './mention-source-types';
import { useMentionEntitySource } from './useMentionEntitySource';
import { useMentionWatchlistSource } from './useMentionWatchlistSource';

export interface MentionSourceBridgeResult {
  sections: MentionPopupSection[];
  loading: boolean;
  onQueryChange: (query: MentionQuery | null) => void;
  renderCandidate: (
    item: MentionCandidate,
    isFocused: boolean,
    searchQuery: string
  ) => ReactNode;
  renderSectionTitle: (title: string) => ReactNode;
  renderPreview: (mention: Mention) => ReactNode | null;
  unresolvedHintText: (mentionText: string) => string;
}

const toEntityCandidate = (
  candidate: MentionEntityCandidate
): MentionCandidate => ({
  kind: 'entity',
  id: candidate.id,
  label: candidate.label,
  avatarUrl: candidate.entity.logo_urls.light_png,
  meta: candidate.entity,
});

const toWatchlistCandidate = (
  candidate: MentionWatchlistCandidate
): MentionCandidate => ({
  kind: candidate.kind,
  id: candidate.id,
  label: candidate.label,
});

/**
 * Wires the portable, controlled `MentionComposerInput` to this app's real
 * search hooks, and supplies the rich, terminal-specific row/preview
 * renderers. Pass `sections`/`loading`/`onQueryChange` straight through to
 * `MentionComposerInput` while mentions are enabled.
 */
export const useMentionSourceBridge = (
  enabled: boolean
): MentionSourceBridgeResult => {
  const { t } = useTranslation(['chat', 'countries', 'analyze', 'widget']);
  const queryClient = useQueryClient();
  const language = useLanguageStore(state => state.languageSetting);

  const [activeQuery, setActiveQuery] = useState<MentionQuery | null>(null);

  const toThemeCandidate = useCallback(
    (candidate: MentionThemeCandidate): MentionCandidate => ({
      kind: 'theme',
      id: candidate.id,
      label: candidate.label,
      avatarUrl:
        candidate.theme.logo_url ??
        getCachedThemeLogoUrl(queryClient, candidate.id, language),
      accentClassName:
        THEME_FILL_COLOR_CLASS_MAP[candidate.theme.type ?? 'industry'],
      meta: candidate.theme,
    }),
    [language, queryClient]
  );

  const toAssetCandidate = useCallback(
    (candidate: MentionAssetCandidate): MentionCandidate =>
      candidate.kind === 'entity'
        ? toEntityCandidate(candidate)
        : toThemeCandidate(candidate),
    [toThemeCandidate]
  );

  const entitySource = useMentionEntitySource(
    enabled && activeQuery?.trigger === '@'
  );
  const watchlistSource = useMentionWatchlistSource(enabled);

  // Read via a ref (updated every render, below) instead of closing over
  // entitySource/watchlistSource directly, so this stays referentially
  // stable — see the referential-stability note above.
  const sourcesRef = useRef({ entitySource, watchlistSource });
  sourcesRef.current = { entitySource, watchlistSource };

  // Memoized (and only ever depends on stable setters/refs) so
  // MentionComposerInput's props stay referentially stable across renders —
  // an unstable callback here recreates the render-loop bug fixed in
  // useSearchThemes/useRecentList: any consumer that useMemo/useEffect off
  // these props would re-run every render.
  //
  // Calls setQuery/clear synchronously here (like the old composer did in
  // applyTrigger) rather than in a useEffect: an effect runs after paint,
  // by which point sections/loading (derived from activeQuery below) have
  // already rendered the *previous* source state as if it belonged to the
  // new query.
  const onQueryChange = useCallback((query: MentionQuery | null) => {
    setActiveQuery(query);
    const { entitySource, watchlistSource } = sourcesRef.current;
    if (!query) {
      entitySource.clear();
      watchlistSource.clear();
      return;
    }
    if (query.trigger === '@') {
      entitySource.setQuery(query.query);
    } else {
      watchlistSource.setQuery(query.query);
    }
  }, []);

  // Derived directly from the search hooks' own state (not copied through
  // an effect) so switching triggers/queries can never leave stale rows or
  // a stale `loading` value behind — sections/loading are always exactly
  // what the underlying source currently reports for `activeQuery`.
  const sections = useMemo<MentionPopupSection[]>(() => {
    if (!enabled || !activeQuery) {
      return [];
    }
    if (activeQuery.trigger === '@') {
      if (!activeQuery.query) {
        return [
          {
            label: t('chat:mentions.recentSection'),
            items: entitySource.recent.map(toAssetCandidate),
          },
        ];
      }
      return [
        {
          label: t('chat:mentions.assetsSection'),
          items: entitySource.entities.map(toEntityCandidate),
        },
        {
          label: t('chat:mentions.themesSection'),
          items: entitySource.themes.map(toThemeCandidate),
        },
      ];
    }
    return [
      {
        label: t('chat:mentions.listsSection'),
        items: watchlistSource.lists.map(toWatchlistCandidate),
      },
      {
        label: t('chat:mentions.publicListsSection'),
        items: watchlistSource.publicLists.map(toWatchlistCandidate),
      },
    ];
  }, [
    enabled,
    activeQuery,
    entitySource.entities,
    entitySource.themes,
    entitySource.recent,
    watchlistSource.lists,
    watchlistSource.publicLists,
    toAssetCandidate,
    toThemeCandidate,
    t,
  ]);

  const loading = useMemo(() => {
    if (!enabled || !activeQuery) {
      return false;
    }
    return activeQuery.trigger === '@'
      ? entitySource.loading
      : watchlistSource.loading;
  }, [enabled, activeQuery, entitySource.loading, watchlistSource.loading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: entitySource/watchlistSource are fresh objects every render; only an `enabled` transition should clear them.
  useEffect(() => {
    if (!enabled) {
      setActiveQuery(null);
      entitySource.clear();
      watchlistSource.clear();
    }
  }, [enabled]);

  const renderCandidate = useCallback(
    (item: MentionCandidate, isFocused: boolean, searchQuery: string) => {
      if (item.kind === 'entity') {
        return (
          <ListEntityItem
            entity={item.meta as Entity}
            isFocused={isFocused}
            highlighted={searchQuery}
          />
        );
      }
      if (item.kind === 'theme') {
        return (
          <ListThemeItem
            theme={item.meta as Theme}
            isFocused={isFocused}
            highlighted={searchQuery}
          />
        );
      }
      return (
        <DropdownListItem
          label={item.label}
          leadingIcon={<Icon iconName="Watchlist" size={16} />}
          isActive={false}
          isFocused={isFocused}
        />
      );
    },
    []
  );

  const renderSectionTitle = useCallback(
    (title: string) => <ListTitleItem item={{ title }} variant="secondary" />,
    []
  );

  const renderPreview = useCallback((mention: Mention): ReactNode | null => {
    if (mention.type === 'entity') {
      return <AssetOverviewPreviewCard entityPath={mention.id} />;
    }
    if (mention.type === 'theme') {
      return <ThemeOverviewPreviewCard themeId={mention.id} />;
    }
    return null;
  }, []);

  const unresolvedHintText = useCallback(
    (mentionText: string) =>
      t('chat:mentions.severalMatches', { mention: mentionText }),
    [t]
  );

  return useMemo(
    () => ({
      sections,
      loading,
      onQueryChange,
      renderCandidate,
      renderSectionTitle,
      renderPreview,
      unresolvedHintText,
    }),
    [
      sections,
      loading,
      onQueryChange,
      renderCandidate,
      renderSectionTitle,
      renderPreview,
      unresolvedHintText,
    ]
  );
};
